import React, { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { logger } from "@/lib/logger";
import { UploadCloud, Check, Loader2, LogOut, ShieldCheck, Camera, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const DOC_FIELDS = [
  { key: "id_proof_front_url", label: "ID Proof — Front" },
  { key: "id_proof_back_url", label: "ID Proof — Back" },
  { key: "address_proof_front_url", label: "Address Proof — Front" },
  { key: "address_proof_back_url", label: "Address Proof — Back" },
];

const empty = {
  avatar_path: "",
  id_proof_front_url: "",
  id_proof_back_url: "",
  address_proof_front_url: "",
  address_proof_back_url: "",
  permanent_address: "",
  family_member_name: "",
  family_member_phone: "",
  emergency_phone: "",
  pf_number: "",
  aadhar_number: "",
  pan_number: "",
  bank_account: { account_number: "", bank_name: "", ifsc: "", account_holder_name: "" },
};

function AvatarStep({ value, onChange, disabled }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Photo saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo upload failed");
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = value ? `${API}/files?path=${encodeURIComponent(value)}` : null;

  return (
    <div className="flex items-center gap-4 bg-zinc-50/60 border border-zinc-200 rounded-md p-4" data-testid="onb-avatar-card">
      <div className="shrink-0">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Your profile"
            className="w-24 h-24 rounded-full object-cover border-2 border-emerald-500"
            data-testid="onb-avatar-preview"
          />
        ) : (
          <div className="w-24 h-24 rounded-full border-2 border-dashed border-zinc-300 bg-white flex items-center justify-center text-zinc-400" data-testid="onb-avatar-empty">
            <UserIcon className="w-9 h-9" strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
          Profile photo
          <span className="text-red-500">*</span>
          {value && <Check className="w-4 h-4 text-emerald-600" />}
        </div>
        <p className="text-[12px] text-zinc-500 mt-0.5">
          A clear face photo — taken just now or uploaded from your phone. Required to continue.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }}
          disabled={disabled || busy}
          data-testid="onb-avatar-file-input"
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }}
          disabled={disabled || busy}
          data-testid="onb-avatar-camera-input"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraRef.current?.click()}
            disabled={disabled || busy}
            data-testid="onb-avatar-camera"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
            Take photo
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={disabled || busy}
            data-testid="onb-avatar-upload"
          >
            <UploadCloud className="w-3.5 h-3.5 mr-1" />
            Upload from device
          </Button>
        </div>
      </div>
    </div>
  );
}

function DocUploader({ field, value, onChange, disabled }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success(`${field.label} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = value ? `${API}/files?path=${encodeURIComponent(value)}` : null;

  return (
    <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/40">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-semibold text-zinc-900">{field.label}<span className="text-red-500 ml-1">*</span></div>
        {value && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        disabled={disabled || busy}
        data-testid={`onb-input-${field.key}`}
      />
      <div className="mt-2 flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          data-testid={`onb-upload-${field.key}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <UploadCloud className="w-3.5 h-3.5 mr-1" />}
          {value ? "Replace" : "Upload"}
        </Button>
        {previewUrl && (
          <a href={previewUrl} target="_blank" rel="noreferrer" className="text-[11px] text-zinc-500 underline truncate max-w-[140px]" data-testid={`onb-preview-${field.key}`}>
            View
          </a>
        )}
      </div>
    </div>
  );
}

export default function EmployeeOnboardingModal() {
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState(empty);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only block when explicitly false (not undefined while loading user)
  // AND only after the user has acknowledged the T&C — terms come first.
  const needsOnboarding = user && user.profile_completed === false && user.profile_verified !== true && user.terms_accepted === true;

  useEffect(() => {
    if (!needsOnboarding) return;
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        const p = data.profile || {};
        setForm({
          ...empty,
          ...p,
          bank_account: { ...empty.bank_account, ...(p.bank_account || {}) },
        });
      } catch (e) {
        logger.error("Failed to load profile", e);
      } finally {
        setLoaded(true);
      }
    })();
  }, [needsOnboarding]);

  if (!needsOnboarding) return null;

  const missing = [];
  if (!form.avatar_path) missing.push("Profile photo");
  if (!form.id_proof_front_url) missing.push("ID front");
  if (!form.id_proof_back_url) missing.push("ID back");
  if (!form.address_proof_front_url) missing.push("Address front");
  if (!form.address_proof_back_url) missing.push("Address back");
  if (!form.permanent_address.trim()) missing.push("Permanent address");
  if (!form.family_member_name.trim()) missing.push("Family member name");
  if (!form.family_member_phone.trim()) missing.push("Family member phone");
  if (!form.emergency_phone.trim()) missing.push("Emergency phone");
  const isReady = missing.length === 0;

  const setDoc = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));
  const setBank = (k, v) => setForm((f) => ({ ...f, bank_account: { ...f.bank_account, [k]: v } }));

  const submit = async () => {
    if (!isReady) {
      toast.error(`Please complete: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      const ba = payload.bank_account || {};
      const hasBank = ba.account_number || ba.bank_name || ba.ifsc || ba.account_holder_name;
      if (!hasBank) payload.bank_account = null;
      await api.put("/me/profile", payload);
      // Refresh user so the modal unmounts and the verification gate kicks in
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      toast.success("Submitted for review. An administrator will verify your documents shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* non-dismissible */ }}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-auto [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="onboarding-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-zinc-900" />
            <DialogTitle>Submit your profile for verification</DialogTitle>
          </div>
          <DialogDescription>
            Hi {user?.name?.split(" ")[0] || "there"} — please upload your KYC documents and emergency contact details. Once you submit, an administrator will review and verify them before you can use the app. Fields marked <span className="text-red-500">*</span> are mandatory.
          </DialogDescription>
        </DialogHeader>

        {!loaded ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
        ) : (
          <div className="space-y-6">
            {/* Profile photo (mandatory) */}
            <section>
              <div className="label-overline mb-2">Profile photo</div>
              <AvatarStep
                value={form.avatar_path}
                onChange={(v) => setForm((f) => ({ ...f, avatar_path: v }))}
                disabled={saving}
              />
            </section>

            {/* Documents */}
            <section>
              <div className="label-overline mb-2">Documents</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DOC_FIELDS.map((f) => (
                  <DocUploader key={f.key} field={f} value={form[f.key]} onChange={setDoc(f.key)} disabled={saving} />
                ))}
              </div>
            </section>

            {/* Personal */}
            <section>
              <div className="label-overline mb-2">Personal</div>
              <div>
                <Label>Permanent address <span className="text-red-500">*</span></Label>
                <Textarea
                  rows={2}
                  value={form.permanent_address}
                  onChange={(e) => setForm({ ...form, permanent_address: e.target.value })}
                  placeholder="House no., street, city, state, PIN"
                  data-testid="onb-permanent-address"
                />
              </div>
            </section>

            {/* Emergency contact */}
            <section>
              <div className="label-overline mb-2">Emergency contact</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Family member name <span className="text-red-500">*</span></Label>
                  <Input
                    value={form.family_member_name}
                    onChange={(e) => setForm({ ...form, family_member_name: e.target.value })}
                    placeholder="e.g. Anita Sharma"
                    data-testid="onb-family-name"
                  />
                </div>
                <div>
                  <Label>Family member phone <span className="text-red-500">*</span></Label>
                  <Input
                    value={form.family_member_phone}
                    onChange={(e) => setForm({ ...form, family_member_phone: e.target.value })}
                    placeholder="+91 ..."
                    data-testid="onb-family-phone"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Alternative emergency phone <span className="text-red-500">*</span></Label>
                  <Input
                    value={form.emergency_phone}
                    onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })}
                    placeholder="Mandatory · Different from family phone"
                    data-testid="onb-emergency-phone"
                  />
                </div>
              </div>
            </section>

            {/* Optional */}
            <section className="border-t border-zinc-200 pt-4">
              <div className="label-overline mb-2 text-zinc-500">Optional</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>PF number</Label>
                  <Input
                    value={form.pf_number}
                    onChange={(e) => setForm({ ...form, pf_number: e.target.value })}
                    placeholder="Provident Fund number"
                    data-testid="onb-pf"
                  />
                </div>
                <div /> {/* spacer */}
                <div>
                  <Label>Aadhar number <span className="text-zinc-400 text-[10px]">(optional)</span></Label>
                  <Input
                    value={form.aadhar_number}
                    onChange={(e) => setForm({ ...form, aadhar_number: e.target.value.replace(/[^\d\s]/g, "").slice(0, 14) })}
                    placeholder="12-digit Aadhar"
                    inputMode="numeric"
                    className="font-mono"
                    data-testid="onb-aadhar"
                  />
                </div>
                <div>
                  <Label>PAN number <span className="text-zinc-400 text-[10px]">(optional)</span></Label>
                  <Input
                    value={form.pan_number}
                    onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase().slice(0, 10) })}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    className="font-mono uppercase"
                    data-testid="onb-pan"
                  />
                </div>
                <div>
                  <Label>Bank account number</Label>
                  <Input
                    value={form.bank_account.account_number}
                    onChange={(e) => setBank("account_number", e.target.value)}
                    data-testid="onb-bank-account"
                  />
                </div>
                <div>
                  <Label>Bank name</Label>
                  <Input
                    value={form.bank_account.bank_name}
                    onChange={(e) => setBank("bank_name", e.target.value)}
                    data-testid="onb-bank-name"
                  />
                </div>
                <div>
                  <Label>IFSC code</Label>
                  <Input
                    value={form.bank_account.ifsc}
                    onChange={(e) => setBank("ifsc", e.target.value.toUpperCase())}
                    placeholder="HDFC0001234"
                    data-testid="onb-bank-ifsc"
                  />
                </div>
                <div>
                  <Label>Account holder name (as per bank)</Label>
                  <Input
                    value={form.bank_account.account_holder_name}
                    onChange={(e) => setBank("account_holder_name", e.target.value)}
                    data-testid="onb-bank-holder"
                  />
                </div>
              </div>
            </section>

            {missing.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="onb-missing">
                Still missing: {missing.join(", ")}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-row justify-between sm:justify-between gap-2 mt-2">
          <Button
            variant="ghost"
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            className="text-zinc-500"
            data-testid="onb-logout"
          >
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
          <Button
            onClick={submit}
            disabled={!isReady || saving}
            className="bg-zinc-950 hover:bg-zinc-800"
            data-testid="onb-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Submit for review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
